import { z } from "zod";

/* ------------------------------------------------------------------ *
 * Level 3 — persistent records
 * ------------------------------------------------------------------ */

export const MemoryFactSchema = z.object({
  id: z.string(),
  userId: z.string(),
  key: z.string(),
  value: z.unknown(),
  tags: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MemoryFact = z.infer<typeof MemoryFactSchema>;

export const TaskStatusSchema = z.enum(["open", "blocked", "done"]);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskSchema = z.object({
  id: z.string(),
  userId: z.string(),
  title: z.string(),
  status: TaskStatusSchema,
  summary: z.string(),
  facts: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Task = z.infer<typeof TaskSchema>;

export const ConversationTurnSchema = z.object({
  id: z.string(),
  localSessionId: z.string(),
  role: z.enum(["user", "assistant"]),
  text: z.string(),
  createdAt: z.string(),
});
export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

export const ToolEventSchema = z.object({
  id: z.string(),
  localSessionId: z.string(),
  toolName: z.string(),
  args: z.unknown(),
  result: z.unknown(),
  latencyMs: z.number(),
  success: z.boolean(),
  createdAt: z.string(),
});
export type ToolEvent = z.infer<typeof ToolEventSchema>;

export const TopicStatusSchema = z.enum(["open", "dormant", "closed"]);
export type TopicStatus = z.infer<typeof TopicStatusSchema>;

/** The unit a user actually returns to. Spans sessions; a session may touch several. */
export const TopicSchema = z.object({
  id: z.string(),
  userId: z.string(),
  title: z.string(),
  status: TopicStatusSchema,
  summary: z.string(),
  salience: z.number(),
  durability: z.number(),
  touchCount: z.number(),
  entities: z.array(z.string()),
  facts: z.record(z.string(), z.unknown()),
  firstSeenAt: z.string(),
  lastTouchedAt: z.string(),
});
export type Topic = z.infer<typeof TopicSchema>;

export const ObservationKindSchema = z.enum([
  "interaction_style",
  "personal_fact",
  "decision",
  "correction",
  "transient",
]);
export type ObservationKind = z.infer<typeof ObservationKindSchema>;

/** Provisional candidate fact. Promoted on high confidence or corroboration. */
export const ObservationSchema = z.object({
  id: z.string(),
  userId: z.string(),
  key: z.string(),
  value: z.unknown(),
  kind: ObservationKindSchema,
  confidence: z.number(),
  corroborationCount: z.number(),
  status: z.enum(["provisional", "promoted", "rejected"]),
  entities: z.array(z.string()),
  sourceTurnIds: z.array(z.string()),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
});
export type Observation = z.infer<typeof ObservationSchema>;

export type TurnKind = "social" | "substantive" | "unknown";

/* ------------------------------------------------------------------ *
 * Level 1 — always loaded identity/policy packet
 * ------------------------------------------------------------------ */

export const Level1ContextSchema = z.object({
  avatar: z.object({
    id: z.string(),
    name: z.string(),
    role: z.string(),
    responsibility: z.string(),
  }),
  user: z.object({
    id: z.string(),
    preferredName: z.string(),
    /** IANA zone, reported by the browser, so time answers need no tool call. */
    timezone: z.string().default("UTC"),
  }),
  channel: z.object({ type: z.literal("voice") }),
  conversationPolicy: z.object({
    defaultMaxSentences: z.number(),
    oneQuestionAtATime: z.boolean(),
    preferShortTurns: z.boolean(),
    chunkLongExplanations: z.boolean(),
    /** Greetings and acknowledgements get this many sentences. One means "Hi." */
    smallTalkMaxSentences: z.number().default(1),
    /** Asking how someone feels is noise from something that cannot care. */
    askAboutFeelings: z.boolean().default(false),
    /** "Sure thing!", "Great question", "Happy to help". */
    allowFiller: z.boolean().default(false),
  }),
  routingPolicy: z.object({
    delegateForPersistentMemory: z.boolean(),
    delegateForTaskState: z.boolean(),
    delegateForExternalActions: z.boolean(),
    delegateForPermissions: z.boolean(),
  }),
});
export type Level1Context = z.infer<typeof Level1ContextSchema>;

/* ------------------------------------------------------------------ *
 * Level 2 — current session / task
 * ------------------------------------------------------------------ */

export const Level2ContextSchema = z.object({
  localSessionId: z.string(),
  activeTask: z
    .object({
      id: z.string(),
      title: z.string(),
      status: TaskStatusSchema,
      summary: z.string(),
      facts: z.record(z.string(), z.unknown()),
    })
    .optional(),
  sessionSummary: z.string().optional(),
  recentRelevantFacts: z.array(MemoryFactSchema),
  recentTurns: z.array(
    z.object({
      role: z.enum(["user", "assistant"]),
      text: z.string(),
      timestamp: z.string(),
    }),
  ),
});
export type Level2Context = z.infer<typeof Level2ContextSchema>;

/* ------------------------------------------------------------------ *
 * The single Realtime tool
 * ------------------------------------------------------------------ */

export const DELEGATE_TOOL_NAME = "delegate_to_brain";

export const DelegateReasonSchema = z.enum([
  "persistent_memory",
  "task_state",
  // Writes. The original spec was read-only, which meant "move it to Thursday"
  // routed to task_state, came back with the unchanged value and success:true,
  // and the model retried forever because nothing could tell it that writing
  // was never attempted.
  "task_update",
  "memory_write",
  "external_action",
  "permission",
  "deeper_reasoning",
  /** Live information from the public web: prices, news, weather, opening hours. */
  "web_lookup",
  "other",
]);
export type DelegateReason = z.infer<typeof DelegateReasonSchema>;

export const DelegateInputSchema = z.object({
  goal: z.string().min(1),
  reason: DelegateReasonSchema,
  relevant_context: z.string().optional(),
  /** Required for task_update / memory_write. Free-text goals are not parsed. */
  updates: z
    .array(z.object({ key: z.string().min(1), value: z.string() }))
    .optional(),
  /** Which task to change. Defaults to the single open task when omitted. */
  target: z.string().optional(),
  /**
   * How much thinking the request is worth. Set by the model, which has the
   * conversation in front of it; inferred from the goal when omitted. Drives
   * reasoning effort and the timeout budget, so a weather check stays fast and
   * a multi-source comparison is allowed the time it actually needs.
   */
  depth: z.enum(["quick", "deep"]).optional(),
});
export type DelegateInput = z.infer<typeof DelegateInputSchema>;

export const DelegateResultSchema = z.object({
  success: z.boolean(),
  answerMode: z.enum(["answer_user", "ask_user", "cannot_complete", "acknowledge"]),
  facts: z.array(
    z.object({
      key: z.string(),
      value: z.unknown(),
      // "topic" and "history" extend the original spec enum: with layered search a
      // fact can now come from a remembered subject or from the raw transcript,
      // and collapsing those into "memory" would hide which layer answered.
      source: z.enum(["memory", "task", "policy", "computed", "topic", "history"]),
    }),
  ),
  instructionToRealtime: z.string(),
});
export type DelegateResult = z.infer<typeof DelegateResultSchema>;

export const DELEGATE_TOOL_DESCRIPTION =
  "Delegate work to the persistent Delegate brain. Use when answering correctly requires " +
  "user-specific information not already present in the current conversation, persistent memory, " +
  "existing task state, an external action, permission checking, or deeper task planning. " +
  "Do not use for greetings, acknowledgements, ordinary small talk, or simple general-knowledge " +
  "questions. Never claim persistent memory or successful external action unless Delegate confirms it. " +
  "To CHANGE stored state use reason task_update or memory_write and pass the exact fields in `updates` - " +
  "reading with task_state never modifies anything. " +
  "Use reason web_lookup for live public information the current date and time in context cannot answer.";

/**
 * JSON Schema handed to OpenAI Realtime as a `function` tool.
 * Shape per https://platform.openai.com/docs/api-reference/realtime
 */
export const WEB_SEARCH_CAPABILITY = "web_search";

/**
 * The delegate tool's schema depends on the avatar's capabilities.
 *
 * web_lookup is a reason on this tool rather than a tool of its own, so an
 * avatar denied web access must not be offered the reason at all. Leaving it in
 * the enum and refusing at execution would have the model promise a lookup it
 * cannot make, then apologise - worse than never offering it.
 */
export function buildDelegateToolDefinition(opts: { webSearch: boolean }) {
  const reasons = [
    "persistent_memory",
    "task_state",
    "task_update",
    "memory_write",
    "external_action",
    "permission",
    "deeper_reasoning",
    ...(opts.webSearch ? ["web_lookup"] : []),
    "other",
  ];

  const base = structuredClone(delegateToolDefinition) as any;
  base.parameters.properties.reason.enum = reasons;
  if (!opts.webSearch) {
    base.parameters.properties.reason.description =
      "Why delegation is required. task_state READS task state; use task_update to CHANGE it " +
      "and memory_write to store a new fact. You have no web access in this session, so you " +
      "cannot look anything up online.";
    delete base.parameters.properties.depth;
    base.description = base.description.replace(
      " Use reason web_lookup for live public information the current date and time in context cannot answer.",
      " You have no web access in this session.",
    );
  }
  return base;
}

export const delegateToolDefinition = {
  type: "function" as const,
  name: DELEGATE_TOOL_NAME,
  description: DELEGATE_TOOL_DESCRIPTION,
  parameters: {
    type: "object",
    properties: {
      goal: {
        type: "string",
        description: "What the Delegate brain must accomplish, in plain language.",
      },
      reason: {
        type: "string",
        enum: [
          "persistent_memory",
          "task_state",
          "task_update",
          "memory_write",
          "external_action",
          "permission",
          "deeper_reasoning",
          "web_lookup",
          "other",
        ],
        description:
          "Why delegation is required. task_state READS task state; use task_update to CHANGE it " +
          "and memory_write to store a new fact. Reading never modifies anything.",
      },
      updates: {
        type: "array",
        description:
          "Required for task_update and memory_write: the exact fields to set. " +
          'e.g. [{"key":"target_day","value":"Thursday"}]. Use key "status" to change a task status.',
        items: {
          type: "object",
          properties: {
            key: { type: "string" },
            value: { type: "string" },
          },
          required: ["key", "value"],
          additionalProperties: false,
        },
      },
      target: {
        type: "string",
        description: "Title of the task to change. Omit to use the current open task.",
      },
      depth: {
        type: "string",
        enum: ["quick", "deep"],
        description:
          "How much thinking this needs. Use \"quick\" for a single fact that one source answers " +
          "(weather, a score, an opening time, a definition) - it returns in a few seconds. " +
          "Use \"deep\" when the answer requires comparing options, several sources, arithmetic over " +
          "found values, or a recommendation - it is noticeably slower, so only ask for it when the " +
          "question genuinely needs it. Default to quick when unsure.",
      },
      relevant_context: {
        type: "string",
        description: "Short relevant context from the current conversation.",
      },
    },
    required: ["goal", "reason"],
    additionalProperties: false,
  },
};

/**
 * Voices the Realtime API accepts for audio output.
 *
 * Per https://platform.openai.com/docs/api-reference/realtime — marin and cedar
 * are the newest and are what OpenAI recommends for quality; the rest predate
 * gpt-realtime and are kept because a persona may suit one of them better.
 *
 * Validated server-side: an unknown voice makes session creation fail, and the
 * fallback ladder would then quietly drop the voice rather than report it.
 */
export const REALTIME_VOICES = [
  "marin",
  "cedar",
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
] as const;

export type RealtimeVoice = (typeof REALTIME_VOICES)[number];

export const RECOMMENDED_VOICES: readonly string[] = ["marin", "cedar"];

export const isRealtimeVoice = (v: unknown): v is RealtimeVoice =>
  typeof v === "string" && (REALTIME_VOICES as readonly string[]).includes(v);

export const REMEMBER_PREFERENCE_TOOL_NAME = "remember_preference";

export const RememberPreferenceInputSchema = z.object({
  key: z.string().min(1),
  value: z.string().min(1),
  kind: z.enum(["interaction_style", "personal_fact", "decision", "correction"]),
  confidence: z.number().min(0).max(1).optional(),
});
export type RememberPreferenceInput = z.infer<typeof RememberPreferenceInputSchema>;

/**
 * Second Realtime tool - a deliberate, documented deviation from the original
 * "one tool" scope. Interaction feedback ("be shorter", "stop asking so many
 * questions", "call me T") has to be captured in-band and applied to the SAME
 * session, or the user tells you how to behave, hears "got it", and watches
 * nothing change. An out-of-band summariser cannot do that.
 */
export const rememberPreferenceToolDefinition = {
  type: "function" as const,
  name: REMEMBER_PREFERENCE_TOOL_NAME,
  description:
    "Record something the user wants remembered about themselves or about how you should interact with them. " +
    "Call this immediately and without being asked whenever the user states a lasting preference for how you " +
    "should speak (length, tone, pacing, how often you ask questions, what to call them), corrects you, or " +
    "states a durable personal fact. Do not call it for one-off requests about the current sentence, for " +
    "small talk, or to store things the user is only thinking aloud about.",
  parameters: {
    type: "object",
    properties: {
      key: {
        type: "string",
        description:
          "Stable snake_case identifier, e.g. preferred_response_length, preferred_name, question_rate, tone.",
      },
      value: { type: "string", description: "The preference or fact, in a few words." },
      kind: {
        type: "string",
        enum: ["interaction_style", "personal_fact", "decision", "correction"],
        description: "interaction_style for how to talk to them; personal_fact for facts about them.",
      },
      confidence: {
        type: "number",
        description: "0-1. Use >= 0.8 only when the user stated it outright.",
      },
    },
    required: ["key", "value", "kind"],
    additionalProperties: false,
  },
};

/* ------------------------------------------------------------------ *
 * Observability
 * ------------------------------------------------------------------ */

export const DEBUG_EVENT_TYPES = [
  "SESSION_STARTING",
  "SESSION_CONNECTED",
  "SESSION_STOPPED",
  "USER_TURN",
  "ASSISTANT_TURN",
  "TOOL_CALL",
  "TOOL_ARGS",
  "MEMORY_HIT",
  "MEMORY_MISS",
  "TOOL_RESULT",
  "PREFERENCE_APPLIED",
  "COST",
  "SESSION_SUMMARIZED",
  "BRIEFING_ACKNOWLEDGED",
  /** The user spoke first, so the startup check never got a turn. Not a failure. */
  "BRIEFING_SKIPPED",
  "TOOL_ERROR",
  "RESPONSE_STARTED",
  "AUDIO_PLAYING",
  "SPEECH_STARTED",
  "SPEECH_STOPPED",
  "ERROR",
  "RAW",
] as const;
export type DebugEventType = (typeof DEBUG_EVENT_TYPES)[number];

export type DebugEvent = {
  timestamp: string;
  localSessionId: string;
  openaiSessionId?: string;
  eventType: DebugEventType;
  toolCallId?: string;
  toolName?: string;
  durationMs?: number;
  payloadSummary?: unknown;
};

export const STATUS = [
  "DISCONNECTED",
  "CONNECTING",
  "LISTENING",
  "SPEAKING",
  "TOOL_RUNNING",
  "ERROR",
] as const;
export type Status = (typeof STATUS)[number];

